import type { DownloadJobType } from "@volgo/core/contract";
import { useCallback, useEffect, useState } from "react";
import { ackInput, useAcks } from "../policy/ack";
import { useSession } from "../session";
import { Button, LinkButton } from "../ui/Button";
import {
  describeDownloadFailure,
  describeError,
  isApiError,
  matchedTagsOf,
  REMOVED_GALLERY_TEXT,
} from "../ui/errors";
import { isActive } from "./poll";
import { downloadProgress } from "./progress";
import { useDownloadSummaryRefresh } from "./useDownloadSummary";
import { useDownloadWatch, type WatchState } from "./useDownloadWatch";

export interface GalleryDownload {
  gid: number;
  /** undefined until the gallery's jobs are known */
  jobs: DownloadJobType[] | undefined;
  done: DownloadJobType | undefined;
  active: DownloadJobType | undefined;
  /** the newest job, when it failed and nothing newer is running or done */
  failed: DownloadJobType | undefined;
  busy: boolean;
  error: unknown;
  watch: WatchState;
  resume: () => void;
  start: () => void;
}

/**
 * The download state of one gallery, behind the single button of the gallery window:
 * 내려받기 (nothing yet, or the last try failed) -> 받는 중 (a job is queued or running) -> 읽기 (a job is done).
 *
 * `enabled` is false for a blocked gallery and for a warned one that is not confirmed: nothing is asked then.
 * A `POLICY_ACK_REQUIRED` answer is handed to `onAckRequired`, which sends the user to the confirm notice.
 * A `POLICY_BLOCKED` answer (to the request, or as the reason a followed job failed) is handed to `onBlocked`:
 * the gallery became blocked after this page learned about it, and the page must stop showing it as open.
 */
export function useGalleryDownload(
  gid: number,
  enabled: boolean,
  onAckRequired: (matchedTags: string[]) => void,
  onBlocked: () => void,
): GalleryDownload {
  const { client } = useSession();
  const acks = useAcks();
  const refreshSummary = useDownloadSummaryRefresh();
  const [jobs, setJobs] = useState<DownloadJobType[] | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    client.call("downloads.byGallery", { params: { gid } }).then(
      (res) => alive && setJobs(res.items),
      (e: unknown) => {
        if (!alive) return;
        setJobs([]);
        setError(e);
      },
    );
    return () => {
      alive = false;
    };
  }, [client, gid, enabled]);

  const merge = useCallback((job: DownloadJobType) => {
    setJobs((prev) => {
      const list = prev ?? [];
      return list.some((j) => j.id === job.id)
        ? list.map((j) => (j.id === job.id ? job : j))
        : [job, ...list];
    });
  }, []);
  const followed = useCallback(
    (job: DownloadJobType) => {
      merge(job);
      if (job.status === "failed" && job.errorCode === "POLICY_BLOCKED") onBlocked();
    },
    [merge, onBlocked],
  );
  const { state: watch, resume } = useDownloadWatch(client, jobs ?? [], followed);

  const start = useCallback(() => {
    setBusy(true);
    setError(null);
    client
      .call("downloads.create", { body: { gid, route: "archive", ...ackInput(acks, gid) } })
      .then(
        (res) => {
          merge(res.download);
          refreshSummary();
        },
        (e: unknown) => {
          if (isApiError(e, "POLICY_ACK_REQUIRED")) onAckRequired(matchedTagsOf(e));
          else if (isApiError(e, "POLICY_BLOCKED")) onBlocked();
          else setError(e);
        },
      )
      .finally(() => setBusy(false));
  }, [client, gid, acks, merge, onAckRequired, onBlocked, refreshSummary]);

  const done = jobs?.find((j) => j.status === "done");
  const active = jobs?.find(isActive);
  const newest = jobs?.[0];
  const failed = !done && !active && newest?.status === "failed" ? newest : undefined;
  return { gid, jobs, done, active, failed, busy, error, watch, resume, start };
}

/** The gallery download or reader button. */
export function DownloadControls({ dl, removed = false }: { dl: GalleryDownload; removed?: boolean }) {
  if (dl.done) {
    return (
      <LinkButton variant="primary" to={`/read/${dl.gid}`} state={{ inApp: true }}>
        읽기
      </LinkButton>
    );
  }
  if (removed || dl.failed?.errorCode === "GALLERY_REMOVED" || isApiError(dl.error, "GALLERY_REMOVED"))
    return null;
  if (dl.active) {
    return (
      <Button variant="primary" disabled aria-live="polite">
        {downloadProgress(dl.active).text}
      </Button>
    );
  }
  return (
    <Button variant="primary" onClick={dl.start} disabled={dl.busy || dl.jobs === undefined}>
      내려받기
    </Button>
  );
}

/** The line under the buttons: what will happen, what is happening, or what went wrong and what to do. */
export function DownloadNote({ dl, removed = false }: { dl: GalleryDownload; removed?: boolean }) {
  if (dl.done) return <p className="note">다 받았습니다. 읽기를 누르면 리더가 열립니다.</p>;
  if (removed || dl.failed?.errorCode === "GALLERY_REMOVED" || isApiError(dl.error, "GALLERY_REMOVED"))
    return (
      <p className="note note-warn" role="status">
        {REMOVED_GALLERY_TEXT}
      </p>
    );
  if (dl.active) {
    if (dl.watch === "timeout") {
      return (
        <p className="note note-warn" role="status">
          받는 데 오래 걸려서 진행 확인을 멈췄습니다. <Button onClick={dl.resume}>다시 확인</Button>
        </p>
      );
    }
    if (dl.watch === "error") {
      return (
        <p className="note note-warn" role="alert">
          진행 상황을 확인하지 못했습니다. <Button onClick={dl.resume}>다시 확인</Button>
        </p>
      );
    }
    const { label, done, total } = downloadProgress(dl.active);
    return (
      <p className="note" role="status">
        {dl.active.status === "queued"
          ? "차례를 기다리고 있습니다. 전체 다운로드는 최대 9개씩 진행됩니다. "
          : dl.active.stage
            ? `${label} 단계입니다. `
            : ""}
        다 받으면 이 버튼이 읽기로 바뀝니다. 창을 닫아도 계속 받습니다.
        {total && <progress value={done} max={total} aria-label={label} />}
      </p>
    );
  }
  if (dl.error !== null) {
    return (
      <p className="note note-warn" role="alert">
        {describeError(dl.error)}
      </p>
    );
  }
  if (dl.failed) {
    return (
      <p className="note note-warn" role="alert">
        지난번에는 받지 못했습니다. {describeDownloadFailure(dl.failed.errorCode)}
      </p>
    );
  }
  return (
    <p className="note">
      EH/ExH에서 리샘플 화질로 받습니다. 리샘플이 없으면 원본으로 받습니다. 다 받으면 이 버튼이 읽기로
      바뀝니다.
    </p>
  );
}
